import { gateCutWarning } from '../hooks/log/logUtils'
import { capRmsAtNextGate } from '../logic/gateUtils'
import { ScreenPath } from '../navigation'
import AmountEntry from '../ui/log/AmountEntry'
import { ActivityGroupSection } from '../ui/log/ActivitySelect'
import styles from '../ui/log/LogScreen.module.css'
import RestDayCard from '../ui/log/RestDayCard'
import { RestDayConfirm, RestDaySuccess } from '../ui/log/RestDayOverlays'
import VictoryOverlay from '../ui/log/VictoryOverlay'
import AppShell from '../ui/layout/AppShell'
import Overlay, { OverlayCard } from '../ui/primitives/Overlay'
import Button from '../ui/primitives/Button'
import { useLogScreenFlow } from '../hooks/log/useLogScreenFlow'

function cutOffNote(value: number, unitConversion: number, gateRoom: number | null) {
  const { cutRms, creditedRms } = capRmsAtNextGate(value * unitConversion, gateRoom)
  return gateCutWarning(cutRms, creditedRms)
}

function LogConfirmDialog({
  value,
  unit,
  unitConversion,
  tooMuch,
  gateRoom,
  onConfirm,
  onCancel,
}: {
  value: number
  unit: string
  unitConversion: number
  tooMuch: boolean
  gateRoom: number | null
  onConfirm: () => void
  onCancel: () => void
}) {
  const note = cutOffNote(value, unitConversion, gateRoom)
  return (
    <Overlay variant="backdrop" onClose={onCancel}>
      <OverlayCard>
        <div className={styles.pageTitle} style={{ fontSize: 22, textAlign: 'center' }}>
          {tooMuch ? "Woah, that's a lot!" : "You'll hit the gate"}
        </div>
        {tooMuch && (
          <p className={styles.conv} style={{ marginTop: 12, textAlign: 'center', fontSize: 14 }}>
            Are you sure you want to log {value} {unit}s?
          </p>
        )}
        {note && (
          <p className={styles.gateCut} style={{ marginTop: 14 }}>
            {note}
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
          <Button onClick={onConfirm}>Yes, log it</Button>
          <Button variant="ghost" onClick={onCancel}>
            Go back
          </Button>
        </div>
      </OverlayCard>
    </Overlay>
  )
}

export default function LogScreen() {
  const flow = useLogScreenFlow()

  if (flow.challengeEnded) {
    return (
      <AppShell>
        <div className={styles.centerTitle}>
          <div className={styles.pageTitle}>IT IS FINISHED</div>
          <p style={{ color: 'var(--muted)', marginTop: 12, fontSize: 14 }}>
            Logging activities is closed for {flow.challengeContext!.challenge.name}. Thanks for
            joining us, it&apos;s been a blast!
          </p>
        </div>
      </AppShell>
    )
  }

  if (flow.isLoading) {
    return (
      <AppShell>
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px 0' }}>Loading…</p>
      </AppShell>
    )
  }

  if (flow.error || !flow.activities) {
    return (
      <AppShell>
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px 0' }}>
          Could not load activities.
        </p>
      </AppShell>
    )
  }

  return (
    <>
      <AppShell>
        {!flow.selectedActivity ? (
          <>
            <div className={styles.centerTitle}>
              <div className={styles.pageTitle}>Log an activity</div>
            </div>

            {flow.restDaysRequired !== undefined &&
              flow.restDaysRequired > 0 &&
              !flow.isActive &&
              (flow.canStart || flow.hasCompletedAllRestDays) && (
                <RestDayCard
                  used={flow.count}
                  total={flow.restDaysRequired}
                  onClick={() => flow.setOverlay('restConfirm')}
                  disabled={flow.hasCompletedAllRestDays}
                />
              )}

            <ActivityGroupSection
              activities={flow.grouped.primary}
              onSelect={flow.selectActivity}
              isDisabled={flow.isActivityDisabled}
            />
            <ActivityGroupSection
              label="More"
              activities={flow.grouped.everyday}
              onSelect={flow.selectActivity}
              isDisabled={flow.isActivityDisabled}
            />
            <ActivityGroupSection
              label="Partner & Team"
              activities={flow.grouped.partner}
              onSelect={flow.selectActivity}
              isDisabled={flow.isActivityDisabled}
            />
            <ActivityGroupSection
              label="Bonus"
              activities={flow.grouped.bonus}
              onSelect={flow.selectActivity}
              isDisabled={flow.isActivityDisabled}
            />
          </>
        ) : (
          <AmountEntry
            key={flow.selectedActivity.id}
            activity={flow.selectedActivity}
            gateRoom={flow.gateRoom}
            onBack={flow.backToSelect}
            onSubmit={(value) => flow.submitLog(flow.selectedActivity!, value)}
          />
        )}
      </AppShell>

      {flow.overlay === 'restConfirm' && (
        <RestDayConfirm
          remaining={flow.remaining}
          onConfirm={() => {
            void flow.confirmRestDay()
          }}
          onCancel={() => flow.setOverlay('none')}
        />
      )}

      {flow.overlay === 'restSuccess' && flow.restDaysRequired !== undefined && (
        <RestDaySuccess
          remaining={flow.remaining - 1}
          total={flow.restDaysRequired}
          used={flow.count + 1}
          onDone={() => {
            flow.setOverlay('none')
            flow.navigate(ScreenPath.home)
          }}
        />
      )}

      {flow.overlay === 'confirmLog' && flow.pendingLog && (
        <LogConfirmDialog
          value={flow.pendingLog.value}
          unit={flow.pendingLog.activity.unit}
          unitConversion={flow.pendingLog.activity.unitConversion}
          tooMuch={
            flow.pendingLog.activity.requiresAmount &&
            flow.pendingLog.value > flow.pendingLog.activity.alertThreshold
          }
          gateRoom={flow.gateRoom}
          onConfirm={() => {
            const pending = flow.pendingLog!
            flow.setPendingLog(null)
            flow.performLog(pending.activity, pending.value)
          }}
          onCancel={() => flow.setOverlay('none')}
        />
      )}

      {flow.overlay === 'victory' && flow.victory && (
        <VictoryOverlay
          quip={flow.victory.quip}
          rms={flow.victory.rms}
          message={flow.victory.message}
          earnedBadge={flow.earnedBadge}
          confettiColors={flow.confettiColors}
          onDone={() => {
            flow.resetFlow()
            flow.navigate(ScreenPath.home)
          }}
          onLogAnother={flow.resetFlow}
        />
      )}
    </>
  )
}
